import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Printer, X, RefreshCw } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';

/**
 * Καθολική ορατότητα εκτυπώσεων στο POS:
 *  - Μόνιμο κόκκινο banner όταν ο print agent του καταστήματος είναι εκτός
 *    σύνδεσης (αποδείξεις/δελτία ΔΕΝ θα τυπωθούν μέχρι να επανέλθει).
 *  - Toast όταν ένα print job αποτύχει οριστικά (μετά και το αυτόματο retry),
 *    με κουμπί άμεσης επανεκτύπωσης.
 * Πριν από αυτό, οι αποτυχίες ήταν εντελώς αόρατες στο προσωπικό.
 */

interface FailedJob {
  jobId: number;
  printerName: string | null;
  jobType: string | null;
  error: string | null;
}

const JOB_TYPE_L: Record<string, string> = {
  receipt: 'Απόδειξη', fiscal: 'Παραστατικό', kitchen: 'Δελτίο κουζίνας',
  ticket: 'Δελτίο', delivery: 'Δελτίο διανομής', register_report: 'Αναφορά ταμείου',
};

const GlobalPrintHealth: React.FC = () => {
  const { socket, isConnected } = useSocket();
  const { store } = useAuth();
  const [agentOffline, setAgentOffline] = useState(false);
  const [failedJobs, setFailedJobs] = useState<FailedJob[]>([]);
  const [reprinting, setReprinting] = useState<number | null>(null);
  const offlineSince = useRef<string | null>(null);

  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` };

  // Αρχική εικόνα: υπάρχει configured agent-printer χωρίς σύνδεση;
  const checkStatus = useCallback(async () => {
    if (!store?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/printers/agents/status`, { headers: authHeaders });
      if (!res.ok) return;
      const data = await res.json();
      // Banner μόνο όταν το κατάστημα ΕΧΕΙ agent-printers και κανένας δεν είναι συνδεδεμένος.
      setAgentOffline((data.totalAgentPrinters || 0) > 0 && (data.connectedCount || 0) === 0);
    } catch { /* best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  useEffect(() => {
    if (!socket || !isConnected) return;
    const onFailed = (j: FailedJob) => {
      setFailedJobs(prev => (prev.some(x => x.jobId === j.jobId) ? prev : [...prev, j]));
    };
    const onOffline = (d: any) => { offlineSince.current = d?.at || null; checkStatus(); };
    const onOnline = () => { offlineSince.current = null; checkStatus(); };
    socket.on('print-job-failed', onFailed);
    socket.on('print-agent-offline', onOffline);
    socket.on('print-agent-online', onOnline);
    return () => {
      socket.off('print-job-failed', onFailed);
      socket.off('print-agent-offline', onOffline);
      socket.off('print-agent-online', onOnline);
    };
  }, [socket, isConnected, checkStatus]);

  const reprint = async (job: FailedJob) => {
    if (!store?.id) return;
    setReprinting(job.jobId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/print-jobs/${job.jobId}/reprint`, {
        method: 'POST', headers: authHeaders,
      });
      if (res.ok) setFailedJobs(prev => prev.filter(x => x.jobId !== job.jobId));
    } catch { /* το toast παραμένει για νέα προσπάθεια */ }
    finally { setReprinting(null); }
  };

  const dismiss = (jobId: number) => setFailedJobs(prev => prev.filter(x => x.jobId !== jobId));

  if (!agentOffline && failedJobs.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] flex flex-col items-center gap-2 pointer-events-none">
      {/* Μόνιμο banner: agent εκτός σύνδεσης */}
      {agentOffline && (
        <div className="pointer-events-auto flex items-center gap-3 bg-red-700 text-white rounded-xl shadow-2xl px-4 py-2.5 border border-red-500">
          <Printer className="w-5 h-5 shrink-0 animate-pulse" />
          <div className="text-sm font-semibold">
            Ο εκτυπωτής είναι ΕΚΤΟΣ ΣΥΝΔΕΣΗΣ — οι αποδείξεις δεν θα τυπώνονται.
            <span className="block text-xs font-normal text-red-100/90">
              Έλεγξε ότι ο υπολογιστής του ταμείου είναι ανοιχτός. Θα τυπωθούν αυτόματα όσες εκκρεμούν μόλις επανέλθει.
            </span>
          </div>
        </div>
      )}

      {/* Toasts: αποτυχημένες εκτυπώσεις με κουμπί επανεκτύπωσης */}
      {failedJobs.slice(0, 3).map(j => (
        <div key={j.jobId} className="pointer-events-auto flex items-center gap-3 bg-surface-1 text-t1 rounded-xl shadow-2xl px-4 py-2.5 border border-amber-500/60">
          <Printer className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0" />
          <div className="text-sm min-w-0">
            <span className="font-semibold">Αποτυχία εκτύπωσης</span>
            {' — '}{JOB_TYPE_L[j.jobType || ''] || 'Εργασία'}{j.printerName ? ` («${j.printerName}»)` : ''}
            <span className="block text-xs text-t3">Έλεγξε χαρτί/ρεύμα και ξαναδοκίμασε.</span>
          </div>
          <button
            onClick={() => reprint(j)}
            disabled={reprinting === j.jobId}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-600 hover:bg-amber-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${reprinting === j.jobId ? 'animate-spin' : ''}`} />
            Επανεκτύπωση
          </button>
          <button onClick={() => dismiss(j.jobId)} className="p-1 text-t3 hover:text-t1">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default GlobalPrintHealth;
